/** Identifier-only browser evidence; independent of the authentication/reporting path. */
import { InsightsFailure, type InsightsFailureClass } from './insightsFailure'
import { getRetainedInsightsEvidence, isCurrentInsightsEvidence } from './insightsEvidence'
import { waitForShopifyOperation } from './shopifyRequestLifecycle'

export type InsightsPhase = 'bridge_ready' | 'id_token' | 'request' | 'response_parse' | 'scope_validation' | 'render'
export type InsightsAcceptedAttempt = {
  attemptId: string
  occurredAt: string
  elapsedMs: number
  scopeKey: string
  status?: number
}
type AttemptOutcome = 'pending' | 'success' | 'error' | 'timeout' | 'cancelled'
export type AttemptRecord = {
  occurredAt: string
  delivery?: 'pending' | 'intake_acknowledged' | 'transport_failed'
  attemptId: string
  phase: InsightsPhase
  elapsedMs: number
  outcome: AttemptOutcome
  status?: number
  failureClass?: InsightsFailureClass
  issueCode?: string
  issuePath?: string
  contentType?: string
  requestId?: string
  phases: { phase: InsightsPhase; elapsedMs: number }[]
}

export type InsightsAttempt = {
  signal: AbortSignal
  phase: (phase: InsightsPhase) => void
  response: (status: number, headers?: Headers) => void
  metadata: () => Pick<AttemptRecord, 'attemptId' | 'occurredAt' | 'elapsedMs' | 'status' | 'contentType' | 'requestId'>
}

const records: AttemptRecord[] = []
const MAX_ATTEMPTS = 20
export const INSIGHTS_DEADLINE_MS = 30_000

/** Detached bounded snapshots contain no store identifiers, URLs, tokens or response values. */
export function getInsightsAttemptDiagnostics() {
  const merged = new Map(
    [...records.filter(isCurrentInsightsEvidence), ...getRetainedInsightsEvidence()].map((record) => [
      record.attemptId + record.phase,
      record,
    ]),
  )
  return [...merged.values()]
    .slice(-MAX_ATTEMPTS)
    .map((record) => ({ ...record, phases: record.phases.map((phase) => ({ ...phase })) }))
}

/** Clear request-local browser history when the verified scope changes. */
export function clearInsightsAttemptHistory() {
  records.length = 0
}

export async function runInsightsAttempt<T>(
  signal: AbortSignal,
  operation: (attempt: InsightsAttempt) => Promise<T>,
  onFailure?: (record: Readonly<AttemptRecord>) => void,
): Promise<T> {
  const controller = new AbortController()
  const started = performance.now()
  const record: AttemptRecord = {
    attemptId: crypto.randomUUID(),
    occurredAt: new Date().toISOString(),
    phase: 'bridge_ready',
    elapsedMs: 0,
    outcome: 'pending',
    phases: [],
  }
  records.push(record)
  if (records.length > MAX_ATTEMPTS) records.shift()
  const elapsed = () => Math.max(0, Math.round(performance.now() - started))
  const cancel = () => controller.abort(signal.reason)
  signal.addEventListener('abort', cancel, { once: true })
  if (signal.aborted) cancel()
  let timedOut = false
  const timer = setTimeout(() => {
    timedOut = true
    controller.abort(new DOMException('Insights load deadline exceeded', 'TimeoutError'))
  }, INSIGHTS_DEADLINE_MS)
  const attempt: InsightsAttempt = {
    signal: controller.signal,
    metadata: () => ({
      attemptId: record.attemptId,
      occurredAt: record.occurredAt,
      elapsedMs: elapsed(),
      status: record.status,
      contentType: record.contentType,
      requestId: record.requestId,
    }),
    phase: (phase) => {
      controller.signal.throwIfAborted()
      record.phase = phase
      record.elapsedMs = elapsed()
      record.phases.push({ phase, elapsedMs: record.elapsedMs })
      console.info('[insights-load]', {
        attemptId: record.attemptId,
        phase,
        elapsedMs: record.elapsedMs,
        outcome: 'pending',
      })
    },
    response: (status, headers) => {
      controller.signal.throwIfAborted()
      record.status = status
      const contentType = headers?.get('content-type')?.split(';')[0]
      if (contentType && /^[a-z0-9.+-]+\/[a-z0-9.+-]+$/i.test(contentType) && contentType.length <= 80)
        record.contentType = contentType
      const requestId = headers?.get('x-request-id')
      if (requestId && /^[a-f0-9-]{16,64}$/i.test(requestId)) record.requestId = requestId
    },
  }
  try {
    const result = await waitForShopifyOperation(controller.signal, () => operation(attempt))
    controller.signal.throwIfAborted()
    record.outcome = 'success'
    return result
  } catch (error) {
    if (timedOut) record.outcome = 'timeout'
    else if (signal.aborted || (error instanceof InsightsFailure && error.failureClass === 'cancelled'))
      record.outcome = 'cancelled'
    else record.outcome = 'error'
    if (timedOut) record.failureClass = 'timeout'
    else if (record.outcome === 'cancelled') record.failureClass = 'cancelled'
    else if (error instanceof InsightsFailure) {
      record.failureClass = error.failureClass
      record.issueCode = error.issueCode
      record.issuePath = error.issuePath
    } else record.failureClass = 'network'
    record.elapsedMs = elapsed()
    if (record.outcome !== 'cancelled') onFailure?.({ ...record, phases: record.phases.map((phase) => ({ ...phase })) })
    throw error
  } finally {
    clearTimeout(timer)
    signal.removeEventListener('abort', cancel)
    record.elapsedMs = elapsed()
    console.info('[insights-load]', { ...record, phases: record.phases.map((phase) => ({ ...phase })) })
  }
}
